import React from 'react';
import SurveyPage from '../components/SurveyPage';
import SurveyTitleArea from '../components/SurveyTitleArea';
import SurveySection from '../components/SurveySection';
import SurveyElement from '../components/SurveyElement';
import SurveyContentContainer from '../components/SurveyContentContainer';
import SurveyGridHeader from '../components/GridComponents/SurveyGridHeader';
import SurveyGridBody from '../components/GridComponents/SurveyGridBody';
import SurveyGridRow from '../components/GridComponents/SurveyGridRow';
import { 
    CommonResponses, ElementType, 
    NumberFormat,
    SelectCombos, SelectCombosPABS,
    PabsMoreInfoPanels,
} from '../Enums/constants';
import SurveyConditionalSegment from '../components/SurveyConditionalSegment';

/**
 * Creates a showIf condition object that checks if a question's response is blank or yes
 * @param {string} questionName - The name of the question to create condition for
 * @returns {Object} An object with the question name as key and array of [BLANK, YES] as value
 * @example
 * showIf={isBlankOrYes('WebCalculatorLinked')}
 */
const isBlankOrYes = (questionName) => ({
    [questionName]: [CommonResponses.BLANK, CommonResponses.YES]
});

export const PabsLgCommunicationTab = () => {
    return (
        <SurveyPage>
            <SurveyTitleArea pageName="Communication"/>
            <SurveyContentContainer>
                <SurveySection id="1" labelText="Calls - Volumes" helpText={PabsMoreInfoPanels.CallsVolumes}>
                    <SurveyElement
                        questionNumber="39"
                        labelText="What were your volumes of:"
                        inputType={ElementType.LABEL}/>
                    <SurveyElement
                        questionNumber="39.a" name="VolIncoming"
                        labelText="Incoming calls that reached and were responded to by a knowledgeable service representative?"
                        note="i.e. exclude messages, etc."
                        placeholder="# incoming calls"
                        allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="39.b" name="VolOutgoing"
                        labelText="Outgoing calls from service representatives responding to messages (voice mall, receptionist, etc.) or following-up previous calls?"
                        placeholder="# outgoing calls"
                        allowNA allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="39.c" name="VolCallsSelfServe"
                        labelText="Incoming calls satisfied by self-serve options, if any?"
                        placeholder="# self-serve calls"
                        allowNA allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="39.d" name="Q_162b"
                        labelText="Email queries from members?"
                        placeholder="# emails"
                        allowNA allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="39.e" name="VolSecureMessages"
                        labelText="Member queries via your secure messaging portal?"
                        placeholder="# secure messages"
                        allowNA allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="39.f" name="VolLiveChat"
                        labelText="Member queries via live chat?"
                        placeholder="# queries"
                        allowNA allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="39.g" name="Written1"
                        labelText="Incoming mail?"
                        placeholder="# mails"
                        allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                </SurveySection>
                <SurveySection id="2" labelText="Calls - Before Reaching a Service Rep">
                    <SurveyElement 
                        questionNumber="40" 
                        labelText="What number of member phone calls did not connect to a person during business hours because:"
                        inputType={ElementType.LABEL}/>
                    <SurveyElement
                        questionNumber="40.a" name="VolBusy"
                        labelText="Busy signal, never enters the system?"
                        placeholder="# busy, unknown, or n/a"
                        allowNA allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="40.b" name="VolLaterBH"
                        labelText="Caller gets pre-recorded 'call another time' message during business hours?"
                        placeholder="# call later, unknown, or n/a"
                        allowNA allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="40.c" name="VolHangup_Menu"
                        labelText="Abandoned calls in a menu?"
                        note="i.e. caller hangs-up before reaching the end of a self-serve menu branch that can answer his/her question or before reaching a queue."
                        placeholder="# caller hang ups, unknown, or n/a"
                        allowNA allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="40.d" name="VolHangup_Queue"
                        labelText="Abandoned calls in a queue or on hold?"
                        note="i.e. caller hangs-up"
                        placeholder="# abandoned calls, unknown, or n/a"
                        allowNA allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="40.e" name="VolBusyAuto"
                        labelText="Busy signal after navigating an automated attendant menu, or after being transferred by a receptionist?"
                        placeholder="# busy signals, unknown, or n/a"
                        allowNA allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="40.f" name="VolUnanswBH"
                        labelText="Call rings unanswered during business hours?"
                        placeholder="# unanswered calls, unknown, or n/a"
                        allowNA allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>

                    <SurveyElement
                        questionNumber="41" name="MenuYN"
                        labelText="Do you have a menu system that callers negotiate before speaking to someone?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                    <SurveyConditionalSegment showIf={isBlankOrYes("MenuYN")}>
                        <SurveyElement
                            questionNumber="41.a" name="Autoa"
                            labelText="What is the average number of menu layers that must be navigated before a caller can speak to a live person?"
                            note="Count each and every time a caller must select a menu option by pressing a button on the phone as a menu layer. Use the volume-weighted average number of menu layers if callers must negotiate different numbers of menu layers to reach a service representative on some menu-tree branches than on others."
                            placeholder="# menu layers"
                            inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                        <SurveyElement
                            questionNumber="41.b" name="Autoc"
                            labelText="What is the average time in seconds that it would take a caller to negotiate the menu and listen to menu options before the caller is forwarded to a live person (or queue for a live person)?"
                            placeholder="# seconds"
                            allowUnknown
                            inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                        <SurveyElement
                            questionNumber="41.c" name="MenuByPass"
                            labelText="Is the menu system by-passed if a service representative is available?"
                            inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>
                        <SurveyElement
                            showIf={isBlankOrYes("MenuByPass")}
                            questionNumber="41.c.1" name="MenuByPassPct"
                            labelText="For what percent of calls by-pass the menu system and go directly to a service representative?"
                            placeholder="% calls"
                            inputType={ElementType.NUMBER} format={NumberFormat.PERCENTAGE} doubleSubQuestion/>
                    </SurveyConditionalSegment>

                    <SurveyElement
                        questionNumber="42" name="ReceptionistFirstContact"
                        labelText="Is a receptionist the first point of human contact when a member calls?"
                        note="A receptionist handles only basic requests like transferring calls, and is not a service representative."
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>

                    <SurveyElement
                        questionNumber="43" name="QueueServiceRep"
                        labelText="Do you have a queue for service representatives?"
                        note="The entry to the queue could be a recorded message, a menu system or a receptionist."
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                    <SurveyElement
                        showIf={isBlankOrYes("QueueServiceRep")}
                        questionNumber="43.a" name="WaitQueueServiceRep"
                        labelText="What is the average wait time in seconds in queue for a service representative?"
                        placeholder="# seconds or unknown"
                        inputType={ElementType.NUMBER}
                        format={NumberFormat.INTEGER}
                        allowUnknown
                        subQuestion />

                    <SurveyElement questionNumber="44" name="WaitTimeNotifyyn"
                                   labelText="Does your system notify the caller of the expected wait time, or their place in the queue, for reaching a knowledgeable service representative if the expected wait exceeds a certain threshold?"
                                   inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>

                    <SurveyElement questionNumber="45" name="CallbackFeature"
                                   labelText="Do you have a callback feature on your phone system?"
                                   note="i.e. the caller's telephone number is placed in a queue for callback when their turn comes up."
                                   inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                    <SurveyConditionalSegment showIf={isBlankOrYes("CallbackFeature")}>
                        <SurveyElement questionNumber="45.a" name="CallbackOnline"
                                       labelText="Can a member request a callback via the website?"
                                       inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>
                        <SurveyElement questionNumber="45.b" name="CallbackFeatureVol"
                                       labelText="What was the total volume of callbacks?"
                                       placeholder="# callbacks"
                                       allowUnknown
                                       inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                        <SurveyElement questionNumber="45.c" name="CallbackCompletion"
                                       labelText="What is the callback completion rate?"
                                       note="i.e. what percent of callers that chose to be called back, were actually reached."
                                       placeholder="% callback completion"
                                       allowUnknown
                                       inputType={ElementType.NUMBER} format={NumberFormat.PERCENTAGE} subQuestion/>
                    </SurveyConditionalSegment>
                </SurveySection>
                <SurveySection id="3" labelText="Calls - After Reaching a Service Rep">
                    <SurveyElement questionNumber="46" labelText="What was the:" inputType={ElementType.LABEL}/>
                    <SurveyElement
                        questionNumber="46.a" name="TalkTime"
                        labelText="Average talk time? (in seconds)"
                        note="Do not include: hold or queuing time prior to a member reaching a knowledgeable person, or hold time after a member has reached a knowledgeable person."
                        placeholder="# seconds or unknown"
                        allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="46.b" name="WrapupTime"
                        labelText="Average after call work time when service representative is unavailable to take another call? (in seconds)"
                        placeholder="# seconds or unknown"
                        allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="46.c" name="AgentOccupancyPct"
                        labelText="Average occupancy %?"
                        note="Time spent handling calls, including after call work, as a percentage of total available time to answer calls."
                        placeholder="% occupancy or unknown"
                        allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.PERCENTAGE} subQuestion/>
                    <SurveyElement
                        questionNumber="46.d" name="Q_150a"
                        labelText="% of calls satisfied by the first contact?"
                        placeholder="% calls or unknown"
                        allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.PERCENTAGE} subQuestion/>

                    <SurveyElement
                        questionNumber="47"
                        labelText="When a member calls in, do your service representatives have real-time access to the following member data:"
                        inputType={ElementType.LABEL}/>
                    <SurveyElement
                        questionNumber="47.a" name="Q_155a"
                        labelText="Interactions via calls and email?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>
                    <SurveyElement
                        questionNumber="47.b" name="Q_155b"
                        labelText="Copies of recent online correspondence?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO_SOME} subQuestion/>
                    <SurveyElement
                        questionNumber="47.c" name="RealTimeWorkflowDigToolsYN"
                        labelText="Use of digital tools (e.g., the pension calculator in the secure area of the website, etc.)?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>
                    <SurveyElement
                        questionNumber="47.d" name="CapabilityMemberStatement"
                        labelText="Most recent member statement?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>
                    <SurveyElement
                        questionNumber="47.e" name="Q_155i"
                        labelText="Beneficiary information?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>

                    <SurveyElement
                        questionNumber="48" name="WorkflowSystem"
                        labelText="Do your service representatives have real-time access to a workflow system that lets them know the status of open items?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>

                    <SurveyElement
                        questionNumber="49"
                        labelText="Do you provide the following information on a real-time basis to members over the phone?"
                        note="If you do not have real-time access to the information or if your policy is not to give the information over the phone because of security or other concerns then your answer should be 'no'."
                        inputType={ElementType.LABEL}/>
                    <SurveyElement
                        questionNumber="49.a" name="Q_156a"
                        labelText="Estimates of the member's pension at retirement?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>
                    <SurveyConditionalSegment showIf={isBlankOrYes("Q_156a")}>
                        <SurveyElement
                            questionNumber="49.a.a1" name="Q_156c"
                            labelText="Can you provide alternate annuity payment options?"
                            note="i.e. joint and 50% survivor, joint and 70% survivor, etc."
                            inputType={ElementType.SELECT} options={SelectCombos.YES_NO} doubleSubQuestion/>
                        <SurveyElement
                            questionNumber="49.a.a2" name="Q_156b"
                            labelText="Is the estimate based on an interactive benefit calculator linked to the member's actual account data?"
                            inputType={ElementType.SELECT} options={SelectCombos.YES_NO} doubleSubQuestion/>
                    </SurveyConditionalSegment>
                    <SurveyElement
                        questionNumber="49.b" name="Q_156aNew"
                        labelText="Transfer value assuming the member exited employment at the time of the call?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>
                    <SurveyElement
                        questionNumber="49.c" name="Q_156bNew"
                        labelText="Pensionable salary?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>
                    <SurveyElement
                        questionNumber="49.d" name="CrmServiceCreditGap"
                        labelText="Service credit history including gaps?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>
                    <SurveyElement
                        questionNumber="49.e" name="Q_157"
                        labelText="Cost to purchase service credit?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>

                    <SurveyElement
                        questionNumber="50"
                        labelText="Can members calling in perform the following transactions over the phone:"
                        inputType={ElementType.LABEL}/>
                    <SurveyElement
                        questionNumber="50.a" name="SACRSChangeAddress"
                        labelText="Change address?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>
                    <SurveyElement
                        questionNumber="50.b" name="ChangeEmailOverPhone"
                        labelText="Change email address?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>
                    <SurveyElement
                        questionNumber="50.c" name="SACRSChangePaymentInstructions"
                        labelText="Change payment instructions?"
                        note="i.e., bank account"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>

                    <SurveyElement
                        questionNumber="51"
                        labelText="Do you have the following call center support technology:"
                        inputType={ElementType.LABEL}/>
                    <SurveyElement
                        questionNumber="51.a" name="WebLiveChat"
                        labelText="Live chat?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>
                    <SurveyElement
                        questionNumber="51.b" name="WebChatbot"
                        labelText="Chatbot or virtual service agent?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>
                    <SurveyElement
                        questionNumber="51.c" name="CoBrowsing"
                        labelText="Co-browsing?"
                        note="Co-browsing enables joint navigation through the secure member area by the customer service representative and member."
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>
                </SurveySection>
                <SurveySection id="4" labelText="Quality and Other">
                    <SurveyElement
                        questionNumber="52" name="Q_158"
                        labelText="Do you review your staff's responses to member calls for coaching purposes on a regular basis?"
                        note="As opposed to intermittent or only while training new service representatives, etc."
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                    <SurveyConditionalSegment showIf={isBlankOrYes("Q_158")}>
                        <SurveyElement
                            questionNumber="52.a" name="CoachingCallsMonthlyFreq"
                            labelText="How many calls per agent per month (on average) do you monitor?"
                            placeholder="# calls per agent per month"
                            inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                        <SurveyElement
                            questionNumber="52.b" name="MonitoringCalls"
                            labelText="Are you listening in on a live call or a recording?"
                            inputType={ElementType.SELECT} options={SelectCombosPABS.LIVE_RECORDING_BOTH} subQuestion
                            saveAsKey={true}/>
                    </SurveyConditionalSegment>

                    <SurveyElement
                        questionNumber="53" name="Q_146"
                        labelText="Do you have a toll free number that members can call to speak with a service representative?"
                        note="Or a number where members are only charged the cost of a local call no matter where they are located."
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>

                    <SurveyElement
                        questionNumber="54" name="Q_152"
                        labelText="How many hours per week is your 'call center' operational?"
                        placeholder="# hours per week"
                        inputType={ElementType.NUMBER} format={NumberFormat.DECIMAL1}/>

                    <SurveyElement
                        questionNumber="55" name="Q_162a"
                        labelText="What is the average time (in business days) to respond to a member query via email or the secure messaging portal?"
                        note="Do not include auto-responses or confirmation of email receipt."
                        placeholder="# business days"
                        inputType={ElementType.NUMBER} format={NumberFormat.DECIMAL1}/>
                </SurveySection>
                <SurveySection id="5" labelText="1-on-1 Counseling">
                    <SurveyElement
                        questionNumber="56" name="DoYouDo1On1"
                        labelText="Do you provide 1-on-1 counseling?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>

                    <SurveyConditionalSegment showIf={isBlankOrYes("DoYouDo1On1")}>
                        <SurveyElement
                            questionNumber="57"
                            labelText="Provide the number of members counseled 1-on-1 that were:"
                            inputType={ElementType.LABEL}/>
                        <SurveyElement
                            questionNumber="57.a" name="InHouse1On1"
                            labelText="Pre-scheduled and/or walk-in counseled in-house?"
                            note="Include only if the member actually sees a counselor. Exclude 'walk-through' traffic (i.e., picking up brochures or forms) where needs can be met by the receptionist."
                            placeholder="# in-house counsels"
                            allowNa allowUnknown
                            inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                        <SurveyElement
                            questionNumber="57.b" name="Q_114c"
                            labelText="In the field at locations separate from the member's place of employment?"
                            placeholder="# in-field counsels"
                            allowNa allowUnknown
                            inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                        <SurveyElement
                            questionNumber="57.c" name="Q_114d"
                            labelText="At the member's place of employment?"
                            placeholder="# counsels at member's employment"
                            allowNa allowUnknown
                            inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                        <SurveyElement
                            questionNumber="57.d" name="Q_114e"
                            labelText="Via teleconference?"
                            note="These are actual sessions with counselors, and may also include pre-scheduled counseling sessions covering materials sent to the member in advance."
                            placeholder="# teleconference counsels"
                            allowNa allowUnknown
                            inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                        <SurveyElement
                            questionNumber="57.e" name="VideoConf1on1"
                            labelText="Online, via videoconference?"
                            placeholder="# online counsels"
                            allowNa allowUnknown
                            inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                        <SurveyElement
                            name="TotalCounseled1on1"
                            labelText="Total counseled 1-on-1"
                            formula="InHouse1On1+Q_114c+Q_114d+Q_114e+VideoConf1on1"
                            inputType={ElementType.CALCULATED} format={NumberFormat.INTEGER} subQuestion/>

                        <SurveyElement
                            questionNumber="58" name="WalkInTraffic"
                            labelText="Do you provide counseling for walk-in traffic?"
                            inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>

                        <SurveyElement
                            questionNumber="59"
                            labelText="For in-person 1-on-1 counseling sessions:"
                            inputType={ElementType.LABEL}/>
                        <SurveyElement
                            questionNumber="59.a" name="InPerson_DoorYN"
                            labelText="Do almost all sessions take place in a private office with a door (versus a cubicle, etc.)?"
                            inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>
                        <SurveyElement
                            questionNumber="59.b" name="InPerson_DataYN"
                            labelText="Do you have real-time access to the member's data (i.e., salary, service credit, refund value, beneficiaries, etc.)?"
                            inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>
                        <SurveyElement
                            questionNumber="59.c" name="InPerson_EstimateYN"
                            labelText="Do you provide new written estimates on a real-time basis for anybody that wants one?"
                            inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>

                        <SurveyElement
                            questionNumber="60"
                            name="FinancialAdvice1on1"
                            labelText="Do you provide financial advice?"
                            inputType={ElementType.SELECT}
                            options={SelectCombos.YES_NO}
                        />
                        <SurveyElement
                            questionNumber="60.a"
                            name="FinancialAdvice1on1MemberVol"
                            labelText="How many members received counseling by a certified financial planner?"
                            inputType={ElementType.NUMBER}
                            format={NumberFormat.INTEGER}
                            placeholder="#"
                            showIf={{
                                FinancialAdvice1on1: { not: CommonResponses.NO }
                            }}
                            allowNA allowUnknown
                            subQuestion
                        />
                    </SurveyConditionalSegment>
                </SurveySection>
                <SurveySection id="6" labelText="Member Presentations" helpText={PabsMoreInfoPanels.MemberPresentations}>
                    <SurveyElement questionNumber="61" name="Q_126"
                                   labelText="Do you offer member presentations (including group counseling)?"
                                   inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                    <SurveyConditionalSegment showIf={isBlankOrYes("Q_126")}>
                        <SurveyElement
                            questionNumber="62"
                            labelText="How many presentations (including group counseling sessions) for members or annuitants [exclude presentations to employers] did you do that took place:"
                            inputType={ElementType.LABEL}/>
                        <SurveyElement
                            questionNumber="62.a" name="PresentationsInHouse"
                            labelText="In-house?"
                            placeholder="# in-house presentations"
                            allowUnknown
                            inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                        <SurveyElement
                            questionNumber="62.b" name="PresentationsInField"
                            labelText="In the field?"
                            placeholder="# in-field presentations"
                            allowUnknown
                            inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                        <SurveyElement
                            questionNumber="62.c" name="PresentationsWebcast"
                            labelText="Online, via live webinar?"
                            note="i.e. a real-time, online presentation that allows two-way communication between the presenter and audience."
                            placeholder="# online presentations"
                            allowUnknown
                            inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                        <SurveyElement
                            name="TotalPresentations"
                            labelText="Total Presentations"
                            formula="PresentationsInHouse+PresentationsInField+PresentationsWebcast"
                            inputType={ElementType.CALCULATED} format={NumberFormat.INTEGER} subQuestion/>

                        <SurveyElement
                            questionNumber="63" name="PresentationAttendeesTotal"
                            labelText="What was the total number of attendees at all of the presentations per the question above?"
                            placeholder="# attendees"
                            inputType={ElementType.NUMBER} format={NumberFormat.INTEGER}/>

                        <SurveyElement
                            questionNumber="64"
                            labelText="Did you do specific presentations for members, in the past fiscal year, targeted solely for:"
                            inputType={ElementType.LABEL}/>
                        <SurveyElement
                            questionNumber="64.a" name="NewMemberPresentations"
                            labelText="New members?"
                            inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>
                        <SurveyElement
                            questionNumber="64.b" name="MidCareerPresentations"
                            labelText="Members in mid career?"
                            inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>
                        <SurveyElement
                            questionNumber="64.c" name="RetirementPresentations"
                            labelText="Members approaching retirement or ready to retire?"
                            inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>
                        <SurveyElement
                            questionNumber="64.d" name="HealthcarePresentations"
                            labelText="Healthcare?"
                            inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>
                        <SurveyElement
                            questionNumber="64.e" name="BenefitChangePresentations"
                            labelText="Changes to benefits?"
                            inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>
                        <SurveyElement
                            questionNumber="64.f" name="OtherPresentations"
                            labelText="Other? (describe)"
                            inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>
                        <SurveyElement
                            showIf={isBlankOrYes("OtherPresentations")}
                            name="OtherPresentationsDescr"
                            placeholder="Description of other presentations"
                            inputType={ElementType.TEXTAREA} doubleSubQuestion/>
                    </SurveyConditionalSegment>
                </SurveySection>
                <SurveySection id="7" labelText="Website" helpText={PabsMoreInfoPanels.Website}>
                    <SurveyElement 
                        questionNumber="65" 
                        labelText="Does your website have a secure member area where:"
                        inputType={ElementType.LABEL}/>
                    <SurveyElement
                        questionNumber="65.a"
                        name="WebSecureAccessActive"
                        labelText="Active members can access their own data?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>
                    <SurveyElement
                        questionNumber="65.b"
                        name="WebSecureAccessInactive"
                        labelText="Inactive members can access their own data?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>
                    <SurveyElement
                        questionNumber="65.c"
                        name="WebSecureAccessAnnuitant"
                        labelText="Annuitants can access their own data?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO} subQuestion/>
                    <SurveyElement
                        questionNumber="65.d"
                        name="WebGreetByName"
                        labelText="Is the member's name prominently displayed on the home page in their secure member area?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>

                    <SurveyElement
                        questionNumber="66"
                        labelText="Is the secure member area customized to only show options relevant to each member group?"
                        note="e.g. active members see different options than annuitants."
                        inputType={ElementType.LABEL}/>
                    <SurveyElement
                        questionNumber="66.a"
                        name="WebPersonalizedAccessActive"
                        labelText="Active members?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>
                    <SurveyElement
                        questionNumber="66.b"
                        name="WebPersonalizedAccessInactive"
                        labelText="Inactive members?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>
                    <SurveyElement
                        questionNumber="66.c"
                        name="WebPersonalizedAccessAnnuitant"
                        labelText="Annuitants?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>

                    <SurveyElement
                        questionNumber="67" name="WebUniqueVisitsToSecureArea"
                        labelText="How many unique members accessed the secure member area in the year?"
                        note="Count a member only once even if he visited multiple times."
                        placeholder="# unique members or unknown"
                        allowNA
                        allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER}/>
                    <SurveyElement labelText="If available, provide the breakdown between:"/>
                    <SurveyElement
                        questionNumber="67.a" name="WebTotalVisitsToSecureAreaActive"
                        labelText="Active members"
                        placeholder="# active members or unknown or n/a"
                        allowNA 
                        allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="67.b" name="WebTotalVisitsToSecureAreaInactive"
                        labelText="Inactive members"
                        placeholder="# inactive members or unknown or n/a"
                        allowNA 
                        allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="67.c" name="WebTotalVisitsToSecureAreaAnnuitant"
                        labelText="Annuitants"
                        placeholder="# annuitants or unknown or n/a"
                        allowNA 
                        allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>

                    <SurveyElement
                        questionNumber="68"
                        name="WebTotalVisitsToSecureArea"
                        labelText="Provide the total number of visits to the secure area of the website:"
                        note="i.e. if one member logs in 3 times then the answer is 3."
                        placeholder="# visits or unknown"
                        allowNA
                        allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER}/>
                    <SurveyElement
                        questionNumber="69"
                        labelText="Which of the following authentication methods are used to access your secure area website?"
                    />
                    <SurveyElement labelText="" inputType={ElementType.LABEL} />
                    <SurveyElement
                        labelText="Primary authentication (required for all users):"
                    />
                    <SurveyElement
                        questionNumber="69.a"
                        name="WebAuthPrimaryUsernamePassword"
                        labelText="Username and password (single factor authentication)"
                        inputType={ElementType.SELECT}
                        options={SelectCombos.YES_NO}
                        subQuestion
                    />
                    <SurveyElement
                        questionNumber="69.b"
                        name="WebAuthPrimaryPasskeyBiometric"
                        labelText="Passkey/biometric authentication(e.g. fingerprint, face recognition)"
                        inputType={ElementType.SELECT}
                        options={SelectCombos.YES_NO}
                        subQuestion
                    />
                    <SurveyElement labelText="" inputType={ElementType.LABEL} />
                    <SurveyElement
                        labelText="Additional authentication factors (multi-factor-authentication)"
                    />
                    <SurveyElement
                        questionNumber="69.c"
                        name="WebAuthMfaAuthenticator"
                        labelText="Authenticator app (e.g. time based one-time password)"
                        inputType={ElementType.SELECT}
                        options={SelectCombos.YES_NO}
                        subQuestion
                    />
                    <SurveyElement
                        questionNumber="69.d"
                        name="WebAuthMfaOneTimeCodeEmail"
                        labelText="One-time code sent via email"
                        inputType={ElementType.SELECT}
                        options={SelectCombos.YES_NO}
                        subQuestion
                    />
                    <SurveyElement
                        questionNumber="69.e"
                        name="WebAuthMfaOneTimeCodeSmsVoice"
                        labelText="One-time code sent via SMS or voice call"
                        inputType={ElementType.SELECT}
                        options={SelectCombos.YES_NO}
                        subQuestion
                    />
                    <SurveyElement
                        questionNumber="69.f"
                        name="WebAuthMfaKnowledgeBased"
                        labelText="Knowledge-based questions"
                        inputType={ElementType.SELECT}
                        options={SelectCombos.YES_NO}
                        subQuestion
                    />
                    <SurveyElement
                        questionNumber="70"
                        labelText="Do you have pension estimate calculator:"
                        inputType={ElementType.LABEL}/>
                    <SurveyElement inputType="grid" styleName={"dataTable communicationTable"}>
                        <SurveyGridHeader>
                            <SurveyElement labelText="" inputType={ElementType.LABEL}/>
                            <SurveyElement labelText="Is it available?" inputType={ElementType.LABEL}/>
                            <SurveyElement labelText="If yes, volume?" inputType={ElementType.LABEL}/>
                        </SurveyGridHeader>
                        <SurveyGridBody>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="70.a"
                                    labelText="In the non-secure area?"
                                    inputType={ElementType.LABEL}/>
                                <SurveyElement
                                    name="WebCalculatorNonSecure"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}/>
                                <SurveyElement
                                    showIf={isBlankOrYes("WebCalculatorNonSecure")}
                                    name="WebCalculatorNonSecureVol"
                                    pageName="Communication"
                                    placeholder="# or unknown or n/a"
                                    allowNA allowUnknown
                                    inputType={ElementType.NUMBER} format={NumberFormat.INTEGER}/>
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="70.b"
                                    labelText="In the secure area, linked to member's salary and service data?"
                                    inputType={ElementType.LABEL}/>
                                <SurveyElement
                                    name="WebCalculatorLinked"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}/>
                                <SurveyElement
                                    showIf={isBlankOrYes("WebCalculatorLinked")}
                                    name="WebCalculatorLinkedVol"
                                    pageName="Communication"
                                    placeholder="# or unknown or n/a"
                                    allowNA allowUnknown
                                    inputType={ElementType.NUMBER} format={NumberFormat.INTEGER}/>
                            </SurveyGridRow>
                            <SurveyGridRow showIf={isBlankOrYes("WebCalculatorLinked")}>
                                <SurveyElement labelText="Can the member:" inputType={ElementType.LABEL}/>
                            </SurveyGridRow><SurveyGridRow showIf={isBlankOrYes("WebCalculatorLinked")}>
                                <SurveyElement
                                    questionNumber="70.b.b1"
                                    labelText="Model different retirement start dates?"
                                    inputType={ElementType.LABEL}
                                    subQuestion/>
                                <SurveyElement
                                    name="WebCalculatorStartDates"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                                <SurveyElement/>
                            </SurveyGridRow>
                            <SurveyGridRow showIf={isBlankOrYes("WebCalculatorLinked")}>
                                <SurveyElement
                                    questionNumber="70.b.b2"
                                    labelText="Model salary changes?"
                                    inputType={ElementType.LABEL}
                                    subQuestion/>
                                <SurveyElement
                                    name="WebCalculatorSalaryChanges"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                                <SurveyElement/>
                            </SurveyGridRow>
                            <SurveyGridRow showIf={isBlankOrYes("WebCalculatorLinked")}>
                                <SurveyElement
                                    questionNumber="70.b.b3"
                                    labelText="Model different working percentages?"
                                    note="e.g. part-time or early termination"
                                    inputType={ElementType.LABEL}
                                    subQuestion/>
                                <SurveyElement
                                    name="WebCalculatorWorkingPercentage"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                                <SurveyElement/>
                            </SurveyGridRow>
                            <SurveyGridRow showIf={isBlankOrYes("WebCalculatorLinked")}>
                                <SurveyElement
                                    questionNumber="70.b.b4"
                                    labelText="Model alternative payment options?"
                                    inputType={ElementType.LABEL}
                                    subQuestion/>
                                <SurveyElement
                                    name="WebCalculatorPaymentOptions"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                                <SurveyElement/>
                            </SurveyGridRow>
                            <SurveyGridRow showIf={isBlankOrYes("WebCalculatorLinked")}>
                                <SurveyElement
                                    questionNumber="70.b.b5"
                                    labelText="Do you have a pension estimate calculator that provides estimates of:"
                                    inputType={ElementType.LABEL}
                                    subQuestion/>
                            </SurveyGridRow>
                            <SurveyGridRow showIf={isBlankOrYes("WebCalculatorLinked")}>
                                <SurveyElement
                                    questionNumber="70.b.b5.b5i"
                                    labelText="Gross and net retirement income?"
                                    inputType={ElementType.LABEL}
                                    doubleSubQuestion/>
                                <SurveyElement
                                    name="WebCalculatorGrossNet"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombosPABS.GROSS_NET_BOTH_NEITHER_NA}/>
                                <SurveyElement/>
                            </SurveyGridRow>
                            <SurveyGridRow showIf={isBlankOrYes("WebCalculatorLinked")}>
                                <SurveyElement
                                    questionNumber="70.b.b5.b5ii"
                                    labelText="Monthly and annual retirement income?"
                                    inputType={ElementType.LABEL}
                                    doubleSubQuestion/>
                                <SurveyElement
                                    name="WebCalculatorMonthlyAnnual"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombosPABS.MONTHLY_ANNUAL_BOTH_NEITHER_NA}/>
                                <SurveyElement/>
                            </SurveyGridRow>
                            <SurveyGridRow showIf={isBlankOrYes("WebCalculatorLinked")}>
                                <SurveyElement
                                    questionNumber="70.b.b5.b5iii"
                                    labelText="Are various estimates run consolidated in a one-page summary for comparative purposes?"
                                    inputType={ElementType.LABEL}
                                    doubleSubQuestion/>
                                <SurveyElement
                                    name="WebCalculatorOnePageSummary"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}/>
                                <SurveyElement/>
                            </SurveyGridRow>
                        </SurveyGridBody>
                    </SurveyElement>

                    <SurveyElement
                        questionNumber="71"
                        labelText="Do you have a:"
                    />
                    <SurveyElement inputType="grid" styleName={"dataTable communicationTable"}>
                        <SurveyGridHeader>
                            <SurveyElement
                                inputType={ElementType.LABEL}
                            />
                            <SurveyElement
                                inputType={ElementType.LABEL}
                            />
                            <SurveyElement
                                inputType={ElementType.LABEL}
                            />
                        </SurveyGridHeader>
                        <SurveyGridBody>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="71.a"
                                    labelText="Prior service credit purchase calculator?"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="WebServiceCreditPurchaseCalculator"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    inputType={ElementType.LABEL}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow
                                showIf={{
                                    WebServiceCreditPurchaseCalculator: ["", "null", CommonResponses.YES]
                                }}
                            >
                                <SurveyElement
                                    questionNumber="71.a.a1"
                                    labelText="Is the purchase estimate final?"
                                    note="i.e., it won&rsquo;t change if the member chooses to purchase within a set time period?"
                                    inputType={ElementType.LABEL}
                                    subQuestion
                                />
                                <SurveyElement
                                    name="WebServiceCreditPurchaseCalculatorFinal"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    inputType={ElementType.LABEL}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="71.b"
                                    labelText="In-service credit purchase calculator?"
                                    note="e.g., maternity, parental, sabbatical, educational, illness leave."
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="WebLeavePurchaseCalculator"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    inputType={ElementType.LABEL}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow
                                showIf={{
                                    WebLeavePurchaseCalculator: ["", "null", CommonResponses.YES]
                                }}
                            >
                                <SurveyElement
                                    questionNumber="71.b.b1"
                                    labelText="Is the purchase estimate final?"
                                    note="i.e., it won't change if the member chooses to purchase within a set time period?"
                                    inputType={ElementType.LABEL}
                                    subQuestion
                                />
                                <SurveyElement
                                    name="WebLeavePurchaseCalculatorFinal"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    inputType={ElementType.LABEL}
                                />
                            </SurveyGridRow>
                        </SurveyGridBody>
                    </SurveyElement>
                    <SurveyElement
                        questionNumber="72"
                        labelText="Indicate whether the following capabilities are available on your website:"
                    />
                    <SurveyElement inputType="grid" styleName={"dataTable communicationTable"}>
                        <SurveyGridHeader>
                            <SurveyElement
                                questionNumber=""
                                labelText=""
                                inputType={ElementType.LABEL}
                            />
                            <SurveyElement
                                labelText="Is it available?"
                                inputType={ElementType.LABEL}
                            />
                            <SurveyElement
                                inputType={ElementType.LABEL}
                            />
                        </SurveyGridHeader>
                        <SurveyGridBody>
                            <SurveyGridRow>
                                <SurveyElement
                                    labelText="View:"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    inputType={ElementType.LABEL}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="72.a"
                                    labelText="The most recent member statement?"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="WebAdobeMemberStatement"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    inputType={ElementType.LABEL}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="72.b"
                                    labelText="Pensionable earnings and/or service without downloading?"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="WebSalaryServiceHTML"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    inputType={ElementType.LABEL}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow
                                showIf={{
                                    WebSalaryServiceHTML: ["", "null", CommonResponses.YES]
                                }}
                            >
                                <SurveyElement
                                    questionNumber="72.b.b1"
                                    labelText="Is salary and service data available?"
                                    inputType={ElementType.LABEL}
                                    subQuestion
                                />
                                <SurveyElement
                                    name="BothSalaryAndService"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    inputType={ElementType.LABEL}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow
                                showIf={{
                                    WebSalaryServiceHTML: ["", "null", CommonResponses.YES]
                                }}
                            >
                                <SurveyElement
                                    questionNumber="72.b.b2"
                                    labelText="Is online data up-to-date to the most recent pay period?"
                                    inputType={ElementType.LABEL}
                                    subQuestion
                                />
                                <SurveyElement
                                    name="CurrentToRecentPayPeriod"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    inputType={ElementType.LABEL}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow
                                showIf={{
                                    WebSalaryServiceHTML: ["", "null", CommonResponses.YES]
                                }}
                            >
                                <SurveyElement
                                    questionNumber="72.b.b3"
                                    labelText="Is a complete annual history from the beginning of employment provided?"
                                    inputType={ElementType.LABEL}
                                    subQuestion
                                />
                                <SurveyElement
                                    name="CompleteHistory"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    inputType={ElementType.LABEL}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="72.c"
                                    labelText="Pension payment details?"
                                    note="i.e., gross amounts, deductions"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="PaymentAdviceOnline"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    inputType={ElementType.LABEL}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="72.d"
                                    labelText="Duplicate tax receipts?"
                                    note="i.e., 1099s in the U.S."
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="WebDuplicateTaxReceipt"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    inputType={ElementType.LABEL}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="72.e"
                                    labelText="History of recent correspondence?"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="PersonalDigitalFileRecentOrMailbox"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    inputType={ElementType.LABEL}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    labelText="Transactions:"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    labelText="Is it available?"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    labelText="Can it be completed online without any staff assistance (excl. internal review/ approval)?"
                                    inputType={ElementType.LABEL}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="72.f"
                                    labelText="Apply for retirement?"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="WebApplyForRetirementClarifiedOnline"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    name="WebApplyForRetirementOnlineWithoutStaff"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO}
                                    showIf={{
                                        WebApplyForRetirementClarifiedOnline: [CommonResponses.BLANK, "null", CommonResponses.YES]
                                    }}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="72.g"
                                    labelText="Change beneficiary?"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="WebChangeBeneficiary"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    name="WebChangeBeneficiaryWithoutStaff"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO}
                                    showIf={{
                                        WebChangeBeneficiary: ["", "null", CommonResponses.YES]
                                    }}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="72.h"
                                    labelText="Change banking information for direct deposit?"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="WebChangeBanking"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    name="WebChangeBankingWithoutStaff"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO}
                                    showIf={{
                                        WebChangeBanking: ["", "null", CommonResponses.YES]
                                    }}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="72.i"
                                    labelText="Change address?"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="WebChangeAddress"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    name="WebChangeAddressWithoutStaff"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO}
                                    showIf={{
                                        WebChangeAddress: ["", "null", CommonResponses.YES]
                                    }}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="72.j"
                                    labelText="Change email address?"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="WebChangeEmail"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    name="WebChangeEmailWithoutStaff"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO}
                                    showIf={{
                                        WebChangeEmail: ["", "null", CommonResponses.YES]
                                    }}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="72.k"
                                    labelText="Change tax withholding amount?"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="WebChangeTaxWithholding"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    name="WebChangeTaxWithholdingWithoutStaff"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO}
                                    showIf={{
                                        WebChangeTaxWithholding: ["", "null", CommonResponses.YES]
                                    }}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="72.l"
                                    labelText="Apply for a transfer-in?"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="WebApplyTransferIn"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    name="WebApplyTransferInWithoutStaff"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO}
                                    showIf={{
                                        WebApplyTransferIn: ["", "null", CommonResponses.YES]
                                    }}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="72.m"
                                    labelText="Apply for a transfer-out?"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="WebApplyRefund"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    name="WebApplyRefundWithoutStaff"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO}
                                    showIf={{
                                        WebApplyRefund: ["", "null", CommonResponses.YES]
                                    }}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="72.n"
                                    labelText="Apply for a prior service credit purchase?"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="WebApplyPurchase"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    name="WebApplyPurchaseWithoutStaff"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO}
                                    showIf={{
                                        WebApplyPurchase: ["", "null", CommonResponses.YES]
                                    }}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="72.o"
                                    labelText="Apply for an in-service credit purchase?"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="WebApplyLeavePurchase"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    name="WebApplyLeavePurchaseWithoutStaff"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO}
                                    showIf={{
                                        WebApplyLeavePurchase: ["", "null", CommonResponses.YES]
                                    }}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="72.p"
                                    labelText="Upload documents?"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="WebUpload"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    inputType={ElementType.LABEL}
                                />
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="72.q"
                                    labelText="Change communication preferences?"
                                    inputType={ElementType.LABEL}
                                />
                                <SurveyElement
                                    name="CommunicationPreferences"
                                    pageName="communication"
                                    inputType={ElementType.SELECT}
                                    options={SelectCombos.YES_NO_NA}
                                />
                                <SurveyElement
                                    inputType={ElementType.LABEL}
                                />
                            </SurveyGridRow>
                        </SurveyGridBody>
                    </SurveyElement>
                    <SurveyConditionalSegment
                        showIf={{
                            WebApplyForRetirementClarifiedOnline: { not: CommonResponses.NO }
                        }}
                    >
                        <SurveyElement
                            questionNumber="73"
                            labelText="How many members submitted their retirement application online?"
                            name="WebApplyForRetirementvolumeClarifiedOnline"
                            pageName="communication"
                            inputType={ElementType.NUMBER}
                            format={NumberFormat.INTEGER}
                            placeholder="#, n/a or unknown"
                            allowNA
                            allowUnknown
                        />
                        <SurveyConditionalSegment
                            showIf={{
                                WebApplyForRetirementvolumeClarifiedOnline: [CommonResponses.NOT_NA]
                            }}
                        >
                            <SurveyElement
                                questionNumber="73.a"
                                labelText="Is the member provided with an estimate, final gross (or net) amount, or neither?"
                                name="WebApplyForRetireClarifEstimateOrFinal"
                                pageName="communication"
                                inputType={ElementType.SELECT}
                                options={SelectCombos.ESTIMATE_FINAL_NEITHER_NA}
                                subQuestion
                            />
                            <SurveyElement
                                questionNumber="73.b"
                                labelText="What proportion of online retirement applications were completed without any manual staff assistance during the online submission and processing stage?"
                                note='"Staff assistance" refers only to actions required during the online application process that prevent the member from completing the retirement application fully online.<br/>It does not include internal reviews or approvals that occur after the application is submitted and are not visible to the member.'
                                name="WebApplyForRetirementFollowup"
                                pageName="communication"
                                inputType={ElementType.NUMBER}
                                format={NumberFormat.PERCENTAGE}
                                placeholder="%"
                                allowNA
                                allowUnknown
                                subQuestion
                            />
                            <SurveyElement
                                questionNumber="73.c"
                                labelText="Can members track the status of their online retirement application?"
                                name="WebStatusRetirement"
                                pageName="communication"
                                inputType={ElementType.SELECT}
                                options={SelectCombos.YES_NO_NA}
                                subQuestion
                            />
                        </SurveyConditionalSegment>
                    </SurveyConditionalSegment>
                </SurveySection>
                <SurveySection id="8" labelText="Outbound Communication">
                    <SurveyElement
                        questionNumber="74"
                        labelText="What % of email addresses do you have in your system for the following groups:"
                        inputType={ElementType.LABEL}/>
                    <SurveyElement
                        questionNumber="74.a" name="EmailsActivePct"
                        labelText="Active members?"
                        placeholder="% or unknown"
                        allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.PERCENTAGE} subQuestion/>
                    <SurveyElement
                        questionNumber="74.b" name="EmailsInactivePct"
                        labelText="Vested inactive members?"
                        placeholder="% or unknown"
                        allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.PERCENTAGE} subQuestion/>
                    <SurveyElement
                        questionNumber="74.b.b1" name="Q_287"
                        labelText="Do you actively keep track of the home addresses of inactive members?"
                        inputType={ElementType.SELECT}
                        options={SelectCombos.YES_NO_NA}
                        allowUnknown
                        doubleSubQuestion/>
                    <SurveyElement
                        questionNumber="74.c" name="EmailsAnnuitantsPct"
                        labelText="Annuitants?"
                        placeholder="% or unknown"
                        allowUnknown
                        inputType={ElementType.NUMBER} format={NumberFormat.PERCENTAGE} subQuestion/>

                    <SurveyElement
                        questionNumber="75"
                        labelText="Do you automatically send targeted emails or letters to members in the following circumstances:"
                        inputType={ElementType.LABEL}/>
                    <SurveyElement inputType="grid" styleName={"dataTable communicationTable"}>
                        <SurveyGridHeader>
                            <SurveyElement labelText="" inputType={ElementType.LABEL}/>
                            <SurveyElement labelText="Active members" inputType={ElementType.LABEL}/>
                            <SurveyElement labelText="Inactive members" inputType={ElementType.LABEL}/>
                            <SurveyElement labelText="Annuitants" inputType={ElementType.LABEL}/>
                        </SurveyGridHeader>
                        <SurveyGridBody>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="75.a"
                                    labelText="Approaching eligibility for retirement?"
                                    inputType={ElementType.LABEL}/>
                                <SurveyElement
                                    name="PersonalizedEligibleToRetireActive"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}/>
                                <SurveyElement
                                    name="PersonalizedEligibleToRetireInactive"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                                <SurveyElement/>
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="75.b"
                                    labelText="Missing beneficiary information?"
                                    inputType={ElementType.LABEL}/>
                                <SurveyElement
                                    name="PersonalizedMissingBeneficiaryActive"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}/>
                                <SurveyElement
                                    name="PersonalizedMissingBeneficiaryInactive"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}/>
                                <SurveyElement
                                    name="PersonalizedMissingBeneficiaryAnnuitant"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}/>
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="75.c"
                                    labelText="Missing email address?"
                                    inputType={ElementType.LABEL}/>
                                <SurveyElement
                                    name="PersonalizedMissingEmailAddress"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}/>
                                <SurveyElement
                                    name="PersonalizedMissingEmailAddressInactive"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                                <SurveyElement
                                    name="PersonalizedMissingEmailAddressAnnuitant"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="75.d"
                                    labelText="Missing address?"
                                    inputType={ElementType.LABEL}/>
                                <SurveyElement/>
                                <SurveyElement
                                    name="PersonalizedMissingAddressInactive"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                                <SurveyElement
                                    name="PersonalizedMissingAddressAnnuitant"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="75.e"
                                    labelText="New members?"
                                    inputType={ElementType.LABEL}/>
                                <SurveyElement
                                    name="Q_184"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}/>
                                <SurveyElement/>
                                <SurveyElement/>
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="75.f"
                                    labelText="Eligibility or likely eligibility, to purchase prior service credit?"
                                    inputType={ElementType.LABEL}/>
                                <SurveyElement
                                    name="PersonalizedEligibleServiceCredit"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}/>
                                <SurveyElement/>
                                <SurveyElement/>
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="75.g"
                                    labelText="Members are vested for pension benefits (if not vested immediately)?"
                                    inputType={ElementType.LABEL}/>
                                <SurveyElement
                                    name="Q_188a"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}/>
                                <SurveyElement/>
                                <SurveyElement/>
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="75.h"
                                    labelText="Eligibility to purchase in-leave service credit?"
                                    inputType={ElementType.LABEL}/>
                                <SurveyElement
                                    name="PersonalizedEligibleLeaveServiceCredit"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}/>
                                <SurveyElement/>
                                <SurveyElement/>
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="75.i"
                                    labelText="Leaving the plan?"
                                    inputType={ElementType.LABEL}/>
                                <SurveyElement/>
                                <SurveyElement
                                    name="PersonalizedInactive"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                                <SurveyElement/>
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="75.j"
                                    labelText="New retirees?"
                                    inputType={ElementType.LABEL}/>
                                <SurveyElement/>
                                <SurveyElement/>
                                <SurveyElement
                                    name="PersonalizedRetirees"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="75.k"
                                    labelText="Pension amount changes?"
                                    inputType={ElementType.LABEL}/>
                                <SurveyElement/>
                                <SurveyElement/>
                                <SurveyElement
                                    name="PersonalizedAmountChange"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="75.l"
                                    labelText="Members are vested for disability benefits (if not vested immediately)?"
                                    inputType={ElementType.LABEL}/>
                                <SurveyElement/>
                                <SurveyElement/>
                                <SurveyElement
                                    name="Q_188b"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}/>
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="75.m"
                                    labelText="Expiration of disability benefits for inactive members"
                                    inputType={ElementType.LABEL}/>
                                <SurveyElement/>
                                <SurveyElement
                                    name="PersonalizedDisabilityExpiry"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}/>
                                <SurveyElement/>
                            </SurveyGridRow>
                            <SurveyGridRow>
                                <SurveyElement
                                    questionNumber="75.n"
                                    labelText="Other milestone communication? (describe below)"
                                    inputType={ElementType.LABEL}/>
                                <SurveyElement/>
                                <SurveyElement
                                    name="Q_188d"
                                    pageName="Communication"
                                    inputType={ElementType.SELECT} options={SelectCombos.YES_NO}/>
                                <SurveyElement/>
                            </SurveyGridRow>
                            <SurveyElement
                                name="Q_188dDesc"
                                pageName="Communication"
                                inputType={ElementType.TEXTAREA}
                                showIf={isBlankOrYes("Q_188d")}
                            />
                        </SurveyGridBody>
                    </SurveyElement>
                    
                    <SurveyElement
                        questionNumber="76"
                        labelText="How many times did you send newsletter by mail or email to the following member segments in the past year:"
                        inputType={ElementType.LABEL}/>
                    <SurveyElement
                        questionNumber="76.a"
                        name="AumQ_176ATargetedNewsletterContributingfreq"
                        labelText="Active members?"
                        placeholder="# or n/a"
                        allowNa
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="76.b"
                        name="AuTargetedNewsletterInactivefreq"
                        labelText="Inactive members?"
                        placeholder="# or n/a"
                        allowNa
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="76.c"
                        name="AumQ_176BTargetedNewsletterPensionerfreq"
                        labelText="Annuitants?"
                        placeholder="# or n/a"
                        allowNa
                        inputType={ElementType.NUMBER} format={NumberFormat.INTEGER} subQuestion/>
                    <SurveyElement
                        questionNumber="77"
                        labelText="Do the following member segments receive a different newsletter from the other member groups?"
                        inputType={ElementType.LABEL}/>
                    <SurveyElement
                        questionNumber="77.a"
                        name="UniqueNewsletterActive"
                        labelText="Active members?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>
                    <SurveyElement
                        questionNumber="77.b"
                        name="UniqueNewsletterInactive"
                        labelText="Inactive members?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>
                    <SurveyElement
                        questionNumber="77.c"
                        name="UniqueNewsletterAnnuitant"
                        labelText="Annuitants?"
                        inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA} subQuestion/>
                </SurveySection>
                <SurveySection id="9" labelText="Member Statements">
                    <SurveyElement
                        questionNumber="78"
                        name="StatementsDistributeMethod"
                        labelText="How do you distribute your member statements?"
                        inputType={ElementType.SELECT}
                        options={SelectCombosPABS.ONLINE_MAIL_BOTH_NA}
                        saveAsKey={true}/>
                        <SurveyConditionalSegment
                            showIf={{
                                StatementsDistributeMethod: ["", "null", "mail", "both"]
                            }}
                        >
                            <SurveyElement
                                questionNumber="79"
                                labelText="Do you mail paper member statements to:"
                                inputType={ElementType.LABEL}/>
                            <SurveyElement
                                questionNumber="79.a"
                                name="StatementsAllActives"
                                labelText="Active members, either all members or all members that have opted in to receiving mail?"
                                inputType={ElementType.SELECT} 
                                options={SelectCombos.YES_NO_NA} 
                                subQuestion/>
                            <SurveyElement
                                questionNumber="79.b"
                                name="StatementsPassiveActives"
                                labelText="Active members who have not accessed the secure member portal in the last year?"
                                inputType={ElementType.SELECT} 
                                options={SelectCombos.YES_NO_NA} 
                                subQuestion/>
                            <SurveyElement
                                questionNumber="79.c"
                                name="StatementsPassiveInactives"
                                labelText="Inactive members, either all members or all members that have opted in to receiving mail?"
                                inputType={ElementType.SELECT} 
                                options={SelectCombos.YES_NO_NA} 
                                subQuestion/>
                            <SurveyElement
                                questionNumber="80"
                                name="Q_181"
                                labelText="On average, how current is an active member's data in the statements that the member receives (in months)?"
                                note="For example, if statements with data current to December 31st are mailed in a staggered mailing beginning May 1st and finishing June 30th, then the members are receiving data that is between 4 and 6 months old, or 5 months old on average."
                                placeholder="# months"
                                inputType={ElementType.NUMBER} format={NumberFormat.INTEGER}/>
                        </SurveyConditionalSegment>
                        <SurveyConditionalSegment
                            showIf={{
                                StatementsDistributeMethod: ["", "null", "online", "both"] // not mail
                            }}
                        >
                            <SurveyElement
                                questionNumber="81"
                                labelText="If member statements are available online:"
                                inputType={ElementType.LABEL}/>
                            <SurveyElement
                                questionNumber="81.a"
                                name="EmailStatementsYN"
                                labelText="Do you send an email notice or text message to members (that have opted out of receiving mail) annually that the member statement is available online?"
                                inputType={ElementType.SELECT}
                                options={SelectCombos.YES_NO_NA}
                                subQuestion/>
                            <SurveyElement
                                questionNumber="81.b"
                                name="StatementsUpdatedRealData"
                                labelText="Is the member statement updated based on real-time data?"
                                inputType={ElementType.SELECT}
                                options={SelectCombos.YES_NO_NA}
                                subQuestion/>
                            <SurveyElement
                                questionNumber="81.b.b1"
                                name="StatementsOnlineDataCurrent"
                                labelText="If no, how current is an active member's data, on average?"
                                inputType={ElementType.NUMBER}
                                format={NumberFormat.DECIMAL1}
                                placeholder="# months, n/a"
                                showIf={{
                                    StatementsUpdatedRealData: ["", "null", CommonResponses.NO]
                                }}
                                allowNA allowUnknown
                                doubleSubQuestion/>
                        </SurveyConditionalSegment>

                    <SurveyConditionalSegment
                        showIf={{
                            StatementsDistributeMethod: [CommonResponses.NOT_NA]
                        }}
                    >
                        <SurveyElement
                            questionNumber="82"
                            labelText="Do your paper or online statements for active members include:"
                            inputType={ElementType.LABEL}/>
                        <SurveyElement
                            questionNumber="82.a"
                            name="Q_183a"
                            labelText="Total accumulated service credit?"
                            inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}
                            subQuestion/>
                        <SurveyElement
                            questionNumber="82.b"
                            name="Q_183b"
                            labelText="Pensionable earnings?"
                            inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}
                            subQuestion/>
                        <SurveyElement
                            questionNumber="82.c"
                            name="Q_183Bonus"
                            labelText="A historical summary of salary and service credit earned each year?"
                            inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}
                            subQuestion/>
                        <SurveyElement
                            questionNumber="82.d"
                            name="Q_183d"
                            labelText="An estimate of the future pension entitlement based on age scenario modeling or assuming the member continues to work until earliest possible retirement?"
                            inputType={ElementType.SELECT} options={SelectCombos.YES_NO_NA}
                            subQuestion/>
                    </SurveyConditionalSegment>
                </SurveySection>
            </SurveyContentContainer>
        </SurveyPage>
    );

}